<script lang="ts" setup>
import illustration404 from '@/assets/svg/hhv3_404.svg'
import illustrationNoData from '@/assets/svg/hhv3_no_hands_found.svg'
import reportWelcomeBannerMobile from '@/assets/analyzer/report_welcome_banner_m.webp'
import reportWelcomeBanner from '@/assets/analyzer/report_welcome_banner.webp'
import reportWelcomeAnalyze from '@/assets/analyzer/report_welcome_analyze.webp'
import reportWelcomeAnalyzeMobile from '@/assets/analyzer/report_welcome_analyze_m.webp'
import reportWelcomeStats from '@/assets/analyzer/report_welcome_stats.webp'
import reportWelcomeStatsMobile from '@/assets/analyzer/report_welcome_stats_m.webp'
import reportWelcomeUpload from '@/assets/analyzer/report_welcome_upload.webp'
import reportWelcomeUploadMobile from '@/assets/analyzer/report_welcome_upload_m.webp'
import GwText from '@/components/lib/base/GwText/GwText.vue'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import { t } from '@/locales/i18n'
import { computed } from 'vue'
import CursorPulseCircle from '@/components/lib/CursorPulseCircle.vue'

export type GwTableRowProps = {
  message:
    | 'error' //
    | 'filter_no_hands'
    | 'upload_hands'
    | 'search_no_reports'
  errorCode?: number | string
  hasRefreshBtn?: boolean
}

defineProps<GwTableRowProps>()

const emit = defineEmits(['click:refresh', 'click:upload'])

const mobile = computed(() => window.innerWidth < 700)

const pngs = [
  {
    src: mobile.value ? reportWelcomeUploadMobile : reportWelcomeUpload,
    text: t('btn.upload'),
  },
  {
    src: mobile.value ? reportWelcomeAnalyzeMobile : reportWelcomeAnalyze,
    text: t('btn.analyze'),
  },
  {
    src: mobile.value ? reportWelcomeStatsMobile : reportWelcomeStats,
    text: t('btn.stats'),
  },
]
</script>

<template>
  <div
    v-if="message === 'error'"
    class="gwtablemessage f-column f-center"
    style="gap: var(--app-gap-2)"
  >
    <illustration404 />

    <GwText
      variant="text_white_large_400"
      class="mt-2"
    >
      {{ t('messages.something_wrong') }}
    </GwText>

    <GwText
      v-if="errorCode"
      variant="text_grey_normal_500"
    >
      {{ t('messages.error_code', { code: errorCode }) }}
    </GwText>

    <GwBtn
      v-if="hasRefreshBtn"
      variant="btn_primary-v2-large"
      icon="mdi-reload"
      class="mt-2"
      @click="emit('click:refresh')"
    >
      {{ t('btn.refresh_this_view') }}
    </GwBtn>
  </div>
  <div
    v-else-if="message === 'filter_no_hands'"
    class="gwtablemessage f-column f-center"
    style="gap: var(--app-gap-2)"
  >
    <illustrationNoData />
    <GwText
      variant="text_white_large_400"
      class="mt-2"
    >
      {{ t('data_table.report_table.no_hands') }}
    </GwText>

    <GwText
      variant="text_grey_normal_500"
      class="text-center mt-1"
      v-html="t('data_table.report_table.no_hands_sub')"
    />
  </div>
  <div
    v-else-if="message === 'search_no_reports'"
    class="gwtablemessage f-column f-center"
    style="gap: var(--app-gap-2)"
  >
    <illustrationNoData />
    <GwText
      variant="text_white_large_400"
      class="mt-2"
    >
      {{ t('data_table.saved_reports_table.no_reports') }}
    </GwText>

    <GwText
      variant="text_grey_normal_500"
      class="text-center mt-1"
      v-html="t('data_table.saved_reports_table.no_reports_sub')"
    />
  </div>
  <div
    v-else-if="message === 'upload_hands'"
    class="welcome_message f-column"
    style="gap: var(--app-gap-6); padding: 4rem"
  >
    <CursorPulseCircle>
      <div
        class="d-flex f-wrap f-center position-relative"
        style="gap: var(--app-gap-6)"
      >
        <img
          v-if="!mobile"
          :src="reportWelcomeBanner"
          alt="banner"
          class="welcome_banner mx-auto w-100"
        />
        <img
          v-else
          :src="reportWelcomeBannerMobile"
          alt="mobile banner"
          style="max-width: 25em"
          class="welcome_banner_mobile mx-auto w-100"
        />
        <div
          class="welcome_text f-column position-absolute"
          style="left: 4rem; gap: var(--app-gap-1)"
        >
          <GwText
            class="welcome_text_title"
            variant="text_white_largest_700"
            v-html="t(`data_table.report_table.welcome${mobile ? '_m' : ''}`)"
          />
          <GwText
            class="welcome_text_sub"
            variant="text_grey_medium_400"
          >
            {{ t('data_table.report_table.welcome_sub') }}
          </GwText>

          <GwBtn
            variant="btn_primary-v2-large"
            icon="mdi-upload"
            class="welcome_text_btn mt-4 mr-auto"
            @click.stop="emit('click:upload')"
          >
            {{ t('btn.upload_hands') }}
          </GwBtn>
        </div>
      </div>
    </CursorPulseCircle>
    <div
      class="welcome_boxes d-flex justify-space-between w-100"
      style="gap: var(--app-pad-6)"
    >
      <CursorPulseCircle
        v-for="png in pngs"
        :key="png.text"
        class="welcome_box position-relative d-flex"
        style="flex-grow: 1"
      >
        <img
          class="w-100 my-auto h-100"
          :src="png.src"
          :alt="png.text"
        />
        <GwText
          variant="text_white_largest_700"
          class="welcome_box_text position-absolute z-index_2"
          style="left: 4rem; bottom: 2rem"
        >
          {{ png.text }}
        </GwText>
      </CursorPulseCircle>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.welcome_message {
  padding: 4rem;
}
.welcome_text {
  &_title {
    --text-size: 3rem;
  }
  &_sub {
    --text-size: 1.175rem;
  }
}
@media (max-width: 1250px) {
  .welcome {
    &_text {
      &_title {
        --text-size: 2.5rem;
      }
      &_sub {
        --text-size: 1rem;
      }
    }
    &_message {
      padding: 1rem !important;
    }
    &_box_text {
      --text-size: 1.75rem;
      left: 1em !important;
      bottom: 1em !important;
    }
  }
}
@media (max-width: 700px) {
  .welcome_boxes {
    flex-direction: column;
    align-items: center;
    max-width: 25em;
    justify-content: flex-start !important;
    gap: 1rem !important;
    margin: auto;
  }
  .welcome {
    &_text {
      left: 1rem !important;
      width: 60%;
      bottom: 0;
      &_btn {
        opacity: 1;
        margin: 1.5rem auto 1.5rem 0 !important;
      }
      &_title {
        --text-size: 1.75rem;
      }
      &_sub {
        --text-size: 0.75rem;
      }
    }
    &_message {
      padding: 0.5rem !important;
      gap: 0.5rem !important;
    }
    &_box {
      &_text {
        left: 1rem !important;
        bottom: 1rem !important;
        --text-size: 1.5rem;
      }
    }
  }
}
@media (max-width: 400px) {
  .welcome_boxes {
    max-width: 20em;
  }
  .welcome_banner_mobile {
    max-width: 20em;
  }
}
</style>
